import { useState, useEffect, useRef } from "react";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  Avatar,
  Typography,
  TextField,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { icons } from "../../constants";
import AddTaskModal from "./addTaskModal";
import { DragDropContext } from "react-beautiful-dnd";
import { getCookie } from "../../utils/getCookie";
import {
  callApiAsync,
  completeTask,
  getCallCurrentUser,
  getTaskYears,
  reorderTaskPosition,
  manageTimeCreateTask,
  manageTimeEditTask,
  rolloverTask,
  getTasksView,
  getSearchTasks,
  getPendingTask,
  getTaskTimeCounts,
  downloadTaskMonthlyReport,
  getAssignableUsersList,
  getNotificationsTask,
  rolloverContinuousTask,
  deleteTask
} from "../../services/apiService";
import { task_status_ENUM } from "../../constants/enums";
import ToastNotification from "../../components/toastNotification";
import TaskSection from "./taskSection";
import { Pagination, Stack, InputAdornment, IconButton } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import CompleteTaskModal from "./completeTaskModal";
import TaskDayCommentsModal from "./taskDayCommentsModal";
import DeleteTaskModal from "./deleteTaskModal";
import BreadCrumbComp from "../../components/breadCrumb";
import TaskNotificationsMenu from "./taskNotification";
import LaddaButton from "../../components/buttons/laddaButton";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { getCurrentDay, getCurrentTime } from "../../services/dateService";
import VerificationModal from "../../components/modals/verificationModal";
import ManagementIndicatorsPanel from "./managementIndicatorsPanel";
import IndicatorsPanel from "./indicatorsPanel";
import { classificationTasksTooltip } from "./tooltipData";
import { StyledTooltip } from "../../components/toolltipComponent";
import {
  getTaskRawState,
  isTaskRawCompleted,
  isTaskDisplayCompleted
} from "./taskHelpers";
import { downloadBlob } from "../../utils/downloadUtils";
import DownloadTaskMonthlyModal from "./downloadTaskMonthlyModal";
import {
  canAssignProcessTasks,
  canCreateOwnTasks,
  canCompleteAssignedTasks,
  canDownloadTaskReport,
  canManageJustifiedActivities,
  canManageTaskCards,
  canViewGlobalManagementIndicators,
  canViewManagementIndicators
} from "../../constants/taskManagerRoles";

const MONTH_NAMES_ES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre"
];

const getYMD = (str) => {
  if (!str) return null;
  const m = String(str)
    .trim()
    .match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return {
    y: Number(m[1]),
    m: Number(m[2]),
    d: Number(m[3])
  };
};

const formatTaskDateRange = (start, end) => {
  const s = getYMD(start);
  const e = getYMD(end);

  if (!s || !e) return "";

  const todayStr = new Date().toISOString().slice(0, 10);
  const t = getYMD(todayStr);

  const sameDate = s.y === e.y && s.m === e.m && s.d === e.d;
  const sameMonth = s.y === e.y && s.m === e.m;
  const sameYear = s.y === e.y;

  const monthName = (m) => MONTH_NAMES_ES[m - 1];

  if (sameDate && s.y === t.y && s.m === t.m && s.d === t.d) {
    return "Hoy";
  }

  if (sameDate) {
    return `${s.d} de ${monthName(s.m)}`;
  }

  if (sameMonth) {
    return `${s.d} al ${e.d} de ${monthName(e.m)}`;
  }
  if (sameYear) {
    return `${s.d} de ${monthName(s.m)} al ${e.d} de ${monthName(e.m)}`;
  }
  return `${s.d} de ${monthName(s.m)} ${s.y} al ${e.d} de ${monthName(e.m)} ${
    e.y
  }`;
};

const parseDate = (d) => {
  if (!d) return null;
  return d instanceof Date ? new Date(d.getTime()) : new Date(d);
};

const startOfDay = (d) => {
  const dt = new Date(d);
  dt.setHours(0, 0, 0, 0);
  return dt;
};

const startOfWeek = (d) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = (day + 6) % 7;
  const monday = new Date(date);
  monday.setDate(date.getDate() - diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
};

const getPrimarySectionForRange = (start, end) => {
  const s = parseDate(start);
  const e = parseDate(end);
  if (!s || !e) return "diaria";

  if (startOfDay(s).getTime() === startOfDay(e).getTime()) return "diaria";
  if (startOfWeek(s).getTime() === startOfWeek(e).getTime()) return "semanal";
  if (s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth())
    return "mensual";
  return "anual";
};

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

const getMonday = (d) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = (day + 6) % 7;
  const monday = new Date(date);
  monday.setDate(date.getDate() - diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
};

const getFriday = (d) => {
  const m = getMonday(d);
  const fri = new Date(m);
  fri.setDate(m.getDate() + 4);
  fri.setHours(23, 59, 59, 999);
  return fri;
};

const formatDaily = (date) => {
  const weekday = date.toLocaleDateString("es-ES", { weekday: "long" });
  const day = date.getDate();
  const month = date.toLocaleDateString("es-ES", { month: "long" });
  return `${capitalize(weekday)} ${day} de ${capitalize(month)}`;
};

const formatWeekly = (today) => {
  const start = getMonday(today);
  const end = getFriday(today);
  const startWeekday = capitalize(
    start.toLocaleDateString("es-ES", { weekday: "long" })
  );
  const endWeekday = capitalize(
    end.toLocaleDateString("es-ES", { weekday: "long" })
  );
  const startDay = start.getDate();
  const endDay = end.getDate();
  const endMonth = capitalize(
    end.toLocaleDateString("es-ES", { month: "long" })
  );

  return `${startWeekday} ${startDay} al ${endWeekday} ${endDay} de ${endMonth}`;
};

const formatMonthly = (date) => {
  const month = capitalize(date.toLocaleDateString("es-ES", { month: "long" }));
  const year = date.getFullYear();
  return `${month} ${year}`;
};

const formatAnnual = (date) => {
  return `${date.getFullYear()}`;
};

const getSectionSubtitle = (sectionId) => {
  const today = new Date();
  switch (sectionId) {
    case "diaria":
      return formatDaily(today);
    case "semanal":
      return formatWeekly(today);
    case "mensual":
      return formatMonthly(today);
    case "anual":
      return formatAnnual(today);
    default:
      return "";
  }
};

const getCurrentYearValue = () => new Date().getFullYear();
const getCurrentMonthValue = () => new Date().getMonth() + 1;

const extractBlobErrorMessage = async (error, fallbackMessage) => {
  const blob = error?.response?.data;

  if (!(blob instanceof Blob)) {
    return error?.response?.data?.error || fallbackMessage;
  }

  try {
    const text = await blob.text();
    const parsed = JSON.parse(text);
    return parsed?.error || fallbackMessage;
  } catch {
    return fallbackMessage;
  }
};

/** Normaliza cualquier fecha a YYYY-MM-DD para ordenar (acepta MySQL, ISO, etc.). */
const toSortableDate = (v) => {
  if (v == null || v === "") return "";
  const s = String(v).trim();
  const m = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  return m ? m[0] : "";
};

/**
 * Fecha para ordenar:
 * - diaria: primero first_day_in_window (día que se muestra en la vista). Así las continuas
 *   con initial_date en el pasado no salen "primero"; se ordenan por hora del día de hoy.
 * - semanal: initial_date/end_date (pasado primero).
 * - mensual y anual: first_day_in_window si existe, sino initial_date/end_date.
 */
const getTaskSortDate = (t, section) => {
  if (section === "diaria") {
    const d =
      t?.first_day_in_window ??
      t?.initial_date ??
      t?.start_date ??
      t?.end_date ??
      "";
    return toSortableDate(d);
  }
  const useTaskDate = section === "semanal";
  const d = useTaskDate
    ? t?.initial_date ?? t?.start_date ?? t?.end_date ?? ""
    : t?.first_day_in_window ??
      t?.initial_date ??
      t?.start_date ??
      t?.end_date ??
      "";
  return toSortableDate(d);
};

/**
 * Ordena tareas según la vista activa:
 * - diaria: primero por día (pendientes del pasado primero, día más antiguo primero), luego por hora dentro del mismo día.
 * - semanal, mensual, anual: por día (first_day_in_window / initial_date / end_date).
 * - calificación (A, B, C, D): sin reordenar (orden actual).
 */
const sortTasksByView = (tasks, section) => {
  if (!Array.isArray(tasks) || tasks.length === 0) return tasks;
  if (["A", "B", "C", "D"].includes(section)) return [...tasks];

  if (section === "diaria") {
    const toMinutes = (hhmm) => {
      if (!hhmm) return null;
      const str = String(hhmm).trim().slice(0, 5);
      const m = str.match(/^(\d{1,2}):(\d{2})$/);
      if (!m) return null;
      const h = Number(m[1]), mm = Number(m[2]);
      if (h < 0 || h > 23 || mm < 0 || mm > 59) return null;
      return h * 60 + mm;
    };
    return [...tasks].sort((a, b) => {
      const da = getTaskSortDate(a, "diaria");
      const db = getTaskSortDate(b, "diaria");
      // Primero por día: pasado primero (ascendente), luego hoy, luego futuro
      if (da !== db) {
        if (!da) return 1;
        if (!db) return -1;
        return da.localeCompare(db);
      }
      // Mismo día: ordenar por hora (más temprano primero)
      const minA = toMinutes(a.today_start_hour ?? a.start_hour);
      const minB = toMinutes(b.today_start_hour ?? b.start_hour);
      if (minA == null && minB == null) return 0;
      if (minA == null) return 1;
      if (minB == null) return -1;
      return minA - minB;
    });
  }

  if (["semanal", "mensual", "anual"].includes(section)) {
    return [...tasks].sort((a, b) => {
      const da = getTaskSortDate(a, section);
      const db = getTaskSortDate(b, section);
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return da.localeCompare(db);
    });
  }

  return [...tasks];
};

const OganizeTask = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const userSesionData = getCookie("sesion");
  const user_id = userSesionData?.id_usuario ?? null;
  const pageSize = 15;
  const PENDING_PAGE_SIZE = 20;
  const COMPLETED_PAGE_SIZE = 10;
  const userData = useSelector((state) => state.userData.userData);

  const day = getCurrentDay();
  const time = getCurrentTime();

  const [openComments, setOpenComments] = useState(false);
  const [openDeleteVerif, setOpenDeleteVerif] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const onSaveAttemptEndRef = useRef(null);
  const [activeSection, setActiveSection] = useState("diaria");
  const [openModal, setOpenModal] = useState(false);
  const [assignableList, setAssignableList] = useState([]);
  const [pendingTasks, setPendingTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [pendingPage, setPendingPage] = useState(1);
  const [completedPage, setCompletedPage] = useState(1);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteComment, setDeleteComment] = useState("");
  const [pendingMeta, setPendingMeta] = useState({
    total: 0,
    totalPages: 1,
    pageSize
  });
  const [completedMeta, setCompletedMeta] = useState({
    total: 0,
    totalPages: 1,
    pageSize
  });
  const [pendingTotalPages, setPendingTotalPages] = useState(1);
  const [completedTotalPages, setCompletedTotalPages] = useState(1);
  const [user, setUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [openCompleteModal, setOpenCompleteModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [pendingTaskNumber, setPendigTask] = useState({});
  const [openDownloadModal, setOpenDownloadModal] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [taskYearOptions, setTaskYearOptions] = useState([]);
  const [downloadYear, setDownloadYear] = useState(getCurrentYearValue());
  const [downloadMonth, setDownloadMonth] = useState(getCurrentMonthValue());
  const [toastConfig, setToastConfig] = useState({
    severity: "success",
    summary: "",
    detail: ""
  });
  const [timeCounts, setTimeCounts] = useState({
    diaria: 0,
    semanal: 0,
    mensual: 0,
    anual: 0
  });

  const isIndicatorSection = (section) =>
    section === "indicadores" || section === "indicadores_gerenciales";

  const roleList = userData?.roleList ?? [];
  const canManageJustifiedTaskActivities = canManageJustifiedActivities(roleList);
  const canSeeManagementIndicators = canViewManagementIndicators(roleList);
  const canSeeGlobalManagementIndicators =
    canViewGlobalManagementIndicators(roleList);
  const canDownloadTaskReportButton = canDownloadTaskReport(roleList);
  const canAssignTasks = canAssignProcessTasks(roleList);
  const canCreateTasks = canAssignTasks || canCreateOwnTasks(roleList);
  const canEditTaskCards = canManageTaskCards(roleList);
  const canCompleteTasks = canCompleteAssignedTasks(roleList);

  const getTaskAssigneeId = (task) =>
    Number(
      task?.employer_in_charge ??
        task?.employee ??
        task?.employer?.id ??
        task?.visible_as_user_id
    );

  const canEditTask = (task) =>
    canEditTaskCards ||
    (canCompleteTasks && Number(user_id) === getTaskAssigneeId(task));

  const isSearching = (searchTerm || "").trim().length > 0;

  const navigate = useNavigate();

  const countsByClass = pendingTaskNumber?.counts || {};
  const getCountByClassification = (id) => countsByClass?.[id] ?? 0;

  const handleChangeSection = (id) => {
    setActiveSection(id);
    setSearchTerm("");
    setPendingPage(1);
    setCompletedPage(1);
  };

  const getCountBySection = (sectionId) => {
    if (!sectionId) return 0;
    return Number(timeCounts?.[sectionId] ?? 0);
  };

  const sections = [
    { id: "diaria", label: "Diaria", icon: icons.dia_soleado },
    { id: "semanal", label: "Semanal", icon: icons.seven_dias },
    { id: "mensual", label: "Mensual", icon: icons.seven_dias_2 },
    { id: "anual", label: "Anual", icon: icons.calendario_año }
  ];

  const classifications = [
    { id: "A", label: "Urgente e Importante", icon: icons.menu_barras },
    { id: "B", label: "Importante pero No Urgente", icon: icons.menu_barras },
    { id: "C", label: "No Importante, Urgente", icon: icons.menu_barras },
    { id: "D", label: "No Importante, No urgente", icon: icons.menu_barras }
  ];

  const loadTaskData = async () => {
    const q = (searchTerm || "").trim();
    const todayISO = getCurrentDay();

    if (q) {
      await callApiAsync(
        () => getSearchTasks(q, 1, pageSize, { user_id }),
        (resp) => {
          const data = resp?.data ?? resp;
          const allItems = [
            ...(data?.items_pending || []),
            ...(data?.items_completed || [])
          ];
          setPendingTasks(allItems);
          setCompletedTasks([]);
          setPendingMeta({ total: allItems.length, totalPages: 1, pageSize });
          setCompletedMeta({ total: 0, totalPages: 1, pageSize });
          getPendingTaskData();
          getTaskTimeCountsData();
        }
      );
      return;
    }

    await callApiAsync(
      () =>
        getTasksView(activeSection, {
          date: todayISO,
          pending_page: pendingPage,
          completed_page: completedPage,
          pending_pageSize: PENDING_PAGE_SIZE,
          completed_pageSize: COMPLETED_PAGE_SIZE,
          employer_id: user_id,
          user_id
        }),
      (resp) => {
        const data = resp?.data ?? resp;

        const normalizeForDaily = (t) => {
          if (activeSection !== "diaria") return t;
          if (t.hasOwnProperty("today_state") && t.today_state != null) {
            const isDoneToday = Number(t.today_state) === 3;
            return {
              ...t,
              raw_completed: isTaskRawCompleted(t),
              raw_state: getTaskRawState(t),
              ui_completed: isDoneToday ? true : isTaskRawCompleted(t),
              ui_state: isDoneToday ? 3 : getTaskRawState(t)
            };
          }
          return t;
        };

        const rawPending = Array.isArray(data?.items_pending)
          ? data.items_pending
          : [];
        const rawCompleted = Array.isArray(data?.items_completed)
          ? data.items_completed
          : [];

        const pending = rawPending
          .map(normalizeForDaily)
          .filter((x) => !isTaskDisplayCompleted(x));
        const completed = rawCompleted
          .map(normalizeForDaily)
          .filter((x) => isTaskDisplayCompleted(x));

        setPendingTasks(pending);
        setCompletedTasks(completed);

        setPendingTotalPages(
          Math.max(
            1,
            Number(data?.pending_totalPages || data?.pending_totalpages || 1)
          )
        );
        setCompletedTotalPages(
          Math.max(
            1,
            Number(
              data?.completed_totalPages || data?.completed_totalpages || 1
            )
          )
        );

        setPendingMeta({
          total: Number(data?.total_pending || 0),
          totalPages: Math.max(1, Number(data?.pending_totalPages || 1)),
          pageSize: Number(data?.pending_pageSize || PENDING_PAGE_SIZE)
        });
        setCompletedMeta({
          total: Number(data?.total_completed || 0),
          totalPages: Math.max(1, Number(data?.completed_totalPages || 1)),
          pageSize: Number(data?.completed_pageSize || COMPLETED_PAGE_SIZE)
        });

        getPendingTaskData();
        getTaskTimeCountsData();
      }
    );
    loadNotificationList(user_id);
  };

  const loadNotificationList = async (currentUserId) => {
    await callApiAsync(
      () => getNotificationsTask(currentUserId),
      (res) => setNotifications(res.notifications || [])
    );
  };

  const closeDeleteVerification = () => {
    setOpenDeleteVerif(false);
    setDeleteComment("");
  };

  const closeDeleteModal = () => {
    setOpenDeleteModal(false);
    setSelectedTask(null);
    setDeleteComment("");
  };

  const rolloverTaskNon = async () => {
    await callApiAsync(
      () => rolloverTask(),
      () => {
        loadTaskData();
      }
    );
  };

  const rolloverContinuousTaskData = async () => {
    await callApiAsync(
      () => rolloverContinuousTask(),
      () => {
        loadTaskData();
      }
    );
  };

  const handleConfirmDelete = ({ comment }) => {
    setDeleteComment(comment || "");
    setOpenDeleteModal(false);
    setOpenDeleteVerif(true);
  };

  const deleteSelectedTask = async () => {
    await callApiAsync(
      () => deleteTask({ id: selectedTask.id, comment: deleteComment, user_id }),
      () => {
        loadTaskData();
        closeDeleteVerification();
        loadNotificationList();
        setToastConfig({
          severity: "success",
          summary: "Éxito",
          detail: "Tarea eliminada correctamente."
        });
        setShowToast(true);
      }
    );
  };

  const getAssignableUsersData = async () => {
    await callApiAsync(
      () => getAssignableUsersList(user_id),
      (data) => {
        setAssignableList(data);
      }
    );
  };

  const getTaskTimeCountsData = async () => {
    const todayISO = getCurrentDay();
    await callApiAsync(
      () => getTaskTimeCounts({ user_id, date: todayISO }),
      (resp) => {
        const data = resp?.data ?? resp;

        setTimeCounts({
          diaria: Number(data?.counts_by_time?.diaria || 0),
          semanal: Number(data?.counts_by_time?.semanal || 0),
          mensual: Number(data?.counts_by_time?.mensual || 0),
          anual: Number(data?.counts_by_time?.anual || 0)
        });
      }
    );
  };

  const getPendingTaskData = async () => {
    await callApiAsync(
      () => getPendingTask(user_id),
      (data) => {
        setPendigTask(data);
        getTaskTimeCountsData();
      }
    );
  };

  const getTaskYearsData = async () => {
    await callApiAsync(
      () => getTaskYears(),
      (resp) => {
        const data = resp?.data ?? resp;
        const currentYear = getCurrentYearValue();
        const normalized = Array.isArray(data) ? data : [];
        const hasCurrentYear = normalized.some(
          (item) => Number(item?.value) === currentYear
        );

        setTaskYearOptions(
          hasCurrentYear
            ? normalized
            : [{ name: String(currentYear), value: currentYear }, ...normalized]
        );
      }
    );
  };

  const handleDownloadTaskReport = async ({ year, month }) => {
    if (!year || !month) {
      setToastConfig({
        severity: "warning",
        summary: "Campos incompletos",
        detail: "Debes seleccionar un año y un mes para descargar el archivo."
      });
      setShowToast(true);
      return;
    }

    try {
      setDownloadLoading(true);
      const response = await downloadTaskMonthlyReport(year, month, {
        user_id
      });
      const blob =
        response?.data instanceof Blob
          ? response.data
          : new Blob([response?.data], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });

      const monthLabel = MONTH_NAMES_ES[Number(month) - 1] || String(month);
      const fileName = `Task_${year}_${String(month).padStart(2, "0")}_${monthLabel}.xlsx`;

      downloadBlob(blob, fileName);
      setDownloadYear(Number(year));
      setDownloadMonth(Number(month));
      setOpenDownloadModal(false);
      setToastConfig({
        severity: "success",
        summary: "Descarga iniciada",
        detail: `Se generó el archivo de tareas de ${monthLabel} ${year}.`
      });
      setShowToast(true);
    } catch (error) {
      const detail = await extractBlobErrorMessage(
        error,
        "No fue posible generar el descargable de tareas."
      );

      setToastConfig({
        severity: "error",
        summary: "Error de descarga",
        detail
      });
      setShowToast(true);
    } finally {
      setDownloadLoading(false);
    }
  };

  useEffect(() => {
    rolloverTaskNon();
    rolloverContinuousTaskData();
    getPendingTaskData();
    getTaskTimeCountsData();
    getAssignableUsersData();
    loadNotificationList(user_id);
    getTaskYearsData();
  }, []);

  useEffect(() => {
    if (!isSearching && !isIndicatorSection(activeSection)) {
      loadTaskData();
    }
  }, [pendingPage, completedPage, activeSection]);

  useEffect(() => {
    if (!isSearching) return;
    const t = setTimeout(() => {
      setPendingPage(1);
      setCompletedPage(1);
      loadTaskData();
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setPendingPage(1);
    setCompletedPage(1);
  }, [activeSection, searchTerm]);

  useEffect(() => {
    const userSesion = getCookie("sesion");
    const userId = userSesion?.id_usuario;
    if (!userSesion || !userId) return;

    const fetchUser = async () => {
      try {
        const response = await getCallCurrentUser(userId);
        const data = response.data;

        setUser({
          initials: data.nombre
            ? data.nombre
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()
            : "?",
          name: data.nombre || "Usuario",
          role: data.area || "Sin área"
        });
      } catch (error) {
        console.error("Error al obtener usuario:", error);
      }
    };
    fetchUser();
  }, []);

  const handleToggleComplete = async (taskId) => {
    const t =
      pendingTasks.find((x) => x.id === taskId) ||
      completedTasks.find((x) => x.id === taskId);

    if (!t) {
      console.warn("No se encontró la tarea con id:", taskId);
      return;
    }

    if (activeSection === "diaria" && isTaskDisplayCompleted(t)) {
      return;
    }

    setPendingTasks((prev) => prev.filter((x) => x.id !== taskId));
    setCompletedTasks((prev) => [
      {
        ...t,
        raw_completed: true,
        raw_state: 3,
        ui_completed: true,
        ui_state: 3,
        completed: true,
        state: 3
      },
      ...prev
    ]);

    try {
      await callApiAsync(
        () => completeTask({ id: taskId, completed: 1, day, time, user_id }),
        () => {
          loadTaskData();
          getPendingTaskData();
          getTaskTimeCountsData();
          loadNotificationList();
        }
      );
    } catch (err) {
      console.error("Error al completar tarea:", err);
      setCompletedTasks((prev) => prev.filter((x) => x.id !== taskId));
      setPendingTasks((prev) => [{ ...t }, ...prev]);
    }
  };

  const getListByDroppableId = (droppableId) => {
    const [, status] = String(droppableId).split("|");
    return status === "completed" ? completedTasks : pendingTasks;
  };

  const setListByDroppableId = (droppableId, updater) => {
    const [, status] = String(droppableId).split("|");
    if (status === "completed") {
      setCompletedTasks(updater);
    } else {
      setPendingTasks(updater);
    }
  };

  const handleConfirmComplete = async ({ comment, needs_leader_review }) => {
    if (!selectedTask?.id) return;
    await callApiAsync(
      () =>
        completeTask({
          id: selectedTask.id,
          completed: 1,
          comment,
          day,
          time,
          user_id,
          needs_leader_review: !!needs_leader_review
        }),
      () => {
        loadTaskData?.();
        setToastConfig({
          severity: "success",
          summary: "Éxito",
          detail: "Tarea completada."
        });
        setShowToast(true);
        setOpenCompleteModal(false);
        setSelectedTask(null);
        loadNotificationList();
      }
    );
  };

  const handleRequestComplete = (task) => {
    if (!task || isTaskDisplayCompleted(task)) return;
    setSelectedTask(task);
    setOpenCompleteModal(true);
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const { source, destination } = result;
    if (source.droppableId !== destination.droppableId) return;

    const list = getListByDroppableId(source.droppableId);
    if (!Array.isArray(list) || list.length === 0) return;

    const moved = list[source.index];
    if (!moved) return;

    const newList = Array.from(list);
    const [removed] = newList.splice(source.index, 1);
    newList.splice(destination.index, 0, removed);

    setListByDroppableId(source.droppableId, newList);

    const newIndex = destination.index;
    const before = newIndex > 0 ? newList[newIndex - 1] : null;
    const after = newIndex < newList.length - 1 ? newList[newIndex + 1] : null;

    const payload = {
      id: moved.id,
      beforeId: before?.id ?? null,
      afterId: after?.id ?? null,
      user_id
    };

    try {
      await callApiAsync(
        () => reorderTaskPosition(payload),
        () => {
          getTaskTimeCountsData();
          loadTaskData();
        }
      );
    } catch (e) {
      console.error("Error reordenando:", e);
      setListByDroppableId(source.droppableId, list);
    }
  };

  const mainTitle = ["diaria", "semanal", "mensual", "anual"].includes(
    activeSection
  )
    ? sections.find((s) => s.id === activeSection)?.label
    : ["A", "B", "C", "D"].includes(activeSection)
    ? `Clasificación ${activeSection}`
    : "Tareas";

  const mainTooltip = ["A", "B", "C", "D"].includes(activeSection)
    ? () => classificationTasksTooltip(activeSection)
    : null;

  const mainDateRange = getSectionSubtitle(activeSection);

  const handleSaveFromModal = async (newTask) => {
    const toHHMM = (v) => {
      if (!v) return null;
      const m = String(v)
        .trim()
        .match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/);
      if (!m) return null;
      const hh = Number(m[1]),
        mm = Number(m[2]);
      if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
      return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
    };

    const toMinutes = (hhmm) => {
      if (!hhmm) return null;
      const [h, m] = hhmm.split(":").map(Number);
      return h * 60 + m;
    };

    const startHour = toHHMM(newTask.start_hour);
    const endHour = toHHMM(newTask.end_hour);

    if ((startHour && !endHour) || (!startHour && endHour)) {
      setToastConfig({
        severity: "error",
        summary: "Faltan datos",
        detail: "Debes seleccionar hora de inicio y de fin."
      });
      setShowToast(true);
      onSaveAttemptEndRef.current?.();
      return;
    }
    if (startHour && endHour) {
      const s = toMinutes(startHour);
      const e = toMinutes(endHour);
      if (e <= s) {
        setToastConfig({
          severity: "error",
          summary: "Rango inválido",
          detail: "La hora fin debe ser mayor que la hora inicio."
        });
        setShowToast(true);
        onSaveAttemptEndRef.current?.();
        return;
      }
    }

    const computedSectionForStorage = getPrimarySectionForRange(
      newTask.start_date,
      newTask.end_date
    );

    const classification =
      selectedTask?.id != null && selectedTask?.calification != null
        ? selectedTask.calification
        : ["A", "B", "C", "D"].includes(activeSection)
        ? activeSection
        : "A";

    const employeesWithProcess = Array.isArray(newTask?.employeesWithProcess)
      ? newTask.employeesWithProcess
      : [];
    const isMultipleCreate =
      !selectedTask?.id && employeesWithProcess.length > 1;

    const fallbackTaskState =
      selectedTask?.id != null
        ? getTaskRawState(selectedTask)
        : task_status_ENUM.TO_DO;

    const buildTaskWithData = (employee_in_charge_id, processId) => ({
      id: selectedTask?.id || 0,
      user_creator_id: user_id,
      description: newTask.description || "",
      process: processId != null ? processId : newTask.process || "",
      initial_date: newTask.start_date || null,
      end_date: newTask.end_date || null,
      task_continuous: newTask.task_continuous ?? "",
      employee_in_charge_id: Number(employee_in_charge_id),
      task_status: newTask.task_status ?? fallbackTaskState,
      classification,
      dateLabel: formatTaskDateRange(newTask.start_date, newTask.end_date),
      section: computedSectionForStorage || "diaria",

      start_hour: startHour || null,
      end_hour: endHour || null
    });

    if (selectedTask?.id) {
      const assigneeId = Number(newTask?.employee_in_charge ?? user_id);
      const taskWithData = buildTaskWithData(assigneeId, newTask.process);

      await callApiAsync(
        () => manageTimeEditTask(taskWithData),
        (data) => {
          if (data.error_status) {
            setToastConfig({
              severity: "error",
              summary: "Franja horaria ocupada",
              detail: data.error
            });
            setShowToast(true);
            onSaveAttemptEndRef.current?.();
            return;
          }
          loadTaskData();
          getTaskTimeCountsData();
          setToastConfig({
            severity: "success",
            summary: "Éxito",
            detail: "Se cambió correctamente la tarea."
          });
          setShowToast(true);
          setOpenModal(false);
          setSelectedTask(null);
        }
      );
    } else if (isMultipleCreate) {
      let created = 0;
      let firstError = null;
      for (const item of employeesWithProcess) {
        const taskWithData = buildTaskWithData(
          item.employee_in_charge_id,
          item.process_id
        );
        try {
          const res = await manageTimeCreateTask(taskWithData);
          const data = res?.data ?? res;
          if (data?.error_status) {
            if (!firstError) firstError = data.error;
            continue;
          }
          created++;
        } catch (e) {
          if (!firstError)
            firstError = e?.response?.data?.error || e?.message || "Error al crear tarea";
        }
      }
      loadTaskData();
      getTaskTimeCountsData();
      setOpenModal(false);
      setSelectedTask(null);
      if (firstError && created === 0) {
        setToastConfig({
          severity: "error",
          summary: "Error",
          detail: firstError
        });
      } else if (created > 0) {
        setToastConfig({
          severity: "success",
          summary: "Éxito",
          detail:
            created < employeesWithProcess.length
              ? `Se crearon ${created} de ${employeesWithProcess.length} tareas. Algunas franjas estaban ocupadas.`
              : `Se crearon ${created} tarea(s) correctamente.`
        });
      }
      setShowToast(true);
    } else {
      const assigneeId = Number(newTask?.employee_in_charge ?? user_id);
      const taskWithData = buildTaskWithData(assigneeId, newTask.process);

      await callApiAsync(
        () => manageTimeCreateTask(taskWithData),
        (data) => {
          if (data.error_status) {
            setToastConfig({
              severity: "error",
              summary: "Franja horaria ocupada",
              detail: data.error
            });
            setShowToast(true);
            onSaveAttemptEndRef.current?.();
            return;
          }
          loadTaskData();
          getTaskTimeCountsData();
          setToastConfig({
            severity: "success",
            summary: "Éxito",
            detail: "Se creó correctamente la tarea."
          });
          setShowToast(true);
          setOpenModal(false);
          setSelectedTask(null);
        }
      );
    }
  };

  if (!user) return <p>Cargando usuario...</p>;

  return (
    <Box
      display="flex"
      minHeight="100vh"
      bgcolor="#f9f9ff"
      padding={"3vh"}
      flexDirection={"column"}
      sx={{ flexWrap: isMobile ? "wrap" : "nowrap" }}
    >
      <div className="searchTitle align-center w-100p">
        <div className="j-space-between w-100p align-center">
          <BreadCrumbComp />

          <div className="HContainer">
            {canDownloadTaskReportButton && (
              <LaddaButton
                variant="contained"
                width="auto"
                fontSize="12px"
                height="40px"
                borderColor="#6A6AD8"
                color="#6A6AD8"
                backgroundColor={"#ffffff"}
                onClick={() => setOpenDownloadModal(true)}
                margin={"0 5px 0 0"}
              >
                <img
                  className="btn-icon"
                  src={icons.descargar}
                  alt="icon"
                  style={{ marginRight: "10px" }}
                />
                Descargar tareas
              </LaddaButton>
            )}
            {canManageJustifiedTaskActivities && (
              <LaddaButton
                variant="contained"
                width="auto"
                fontSize="12px"
                height="40px"
                borderColor="#6A6AD8"
                color="#6A6AD8"
                backgroundColor={"#ffffff"}
                onClick={() => navigate("/manage_time/justified_activities")}
                margin={"0 5px 0 0"}
              >
                <img
                  className="btn-icon"
                  src={icons.attach_file}
                  alt="icon"
                  style={{ marginRight: "10px" }}
                />{" "}
                Actividades justificadas
              </LaddaButton>
            )}
            <TaskNotificationsMenu
              currentUserId={user_id}
              notifications={notifications}
              setNotifications={setNotifications}
              onOpenTaskDetail={(taskId) => {
                setSelectedTask({ id: taskId });
                setOpenComments(true);
              }}
            />
          </div>
        </div>
      </div>
      <div className="IHContainer">
        <Drawer
          variant="permanent"
          PaperProps={{
            sx: {
              width: 280,
              position: "relative",
              borderRight: "1px solid #eee",
              top: "25px",
              height: "90%",
              border: "1px solid #ddd",
              borderRadius: 2
            }
          }}
        >
          <Box p={2}>
            <Box display="flex" alignItems="center" mb={2}>
              <Avatar sx={{ bgcolor: "#4444B2", mr: 1 }}>
                {user.initials}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight="bold" noWrap>
                  {user.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {user.role}
                </Typography>
              </Box>
            </Box>

            <TextField
              fullWidth
              size="small"
              placeholder="Buscar..."
              sx={{ mb: 2 }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setPendingPage(1);
                  setCompletedPage(1);
                }
              }}
              InputProps={{
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="limpiar búsqueda"
                      onClick={() => {
                        setSearchTerm("");
                        setPendingPage(1);
                        setCompletedPage(1);
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
            />

            <List dense>
              {sections.map((s) => {
                const isActive = activeSection === s.id;
                return (
                  <ListItem
                    button
                    key={s.id}
                    sx={{
                      borderRadius: "8px",
                      backgroundColor: isActive ? "#f0f0ff" : "#fff",
                      border: isActive ? "1px solid #4444B2" : "1px solid #ddd",
                      mb: 1,
                      "&:hover": {
                        backgroundColor: isActive ? "#e8e8ff" : "#f9f9f9"
                      }
                    }}
                    selected={isActive}
                    onClick={() => handleChangeSection(s.id)}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <img
                        src={s.icon}
                        alt={s.label}
                        style={{ width: 18, height: 18 }}
                      />
                    </ListItemIcon>

                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: "bold",
                        flexGrow: 1,
                        color: isActive ? "#4444B2" : "inherit"
                      }}
                    >
                      {s.label}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        bgcolor: "#f1f1f5",
                        px: 1.2,
                        borderRadius: "12px",
                        fontSize: "0.75rem",
                        fontWeight: "bold"
                      }}
                    >
                      {getCountBySection(s.id)}
                    </Typography>
                  </ListItem>
                );
              })}
            </List>

            <ListItem
              button
              key="indicadores"
              sx={{
                borderRadius: "8px",
                backgroundColor: "#fff",
                border: "1px solid #ddd",
                mb: 3,
                "&:hover": { backgroundColor: "#f9f9f9" }
              }}
              onClick={() => handleChangeSection("indicadores")}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <img
                  src={icons.lineChard}
                  alt="Indicadores"
                  style={{ width: 18, height: 18 }}
                />
              </ListItemIcon>
              <Typography
                variant="body2"
                sx={{ fontWeight: "bold", flexGrow: 1 }}
              >
                Indicadores
              </Typography>
            </ListItem>

            {canSeeManagementIndicators && (
              <ListItem
                button
                key="indicadores_gerenciales"
                sx={{
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  border: "1px solid #ddd",
                  mb: 1,
                  "&:hover": { backgroundColor: "#f9f9f9" }
                }}
                onClick={() => handleChangeSection("indicadores_gerenciales")}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <img
                    src={icons.lineChard}
                    alt="Indicadores gerenciales"
                    style={{ width: 18, height: 18 }}
                  />
                </ListItemIcon>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "bold", flexGrow: 1 }}
                >
                  Indicadores gerenciales
                </Typography>
              </ListItem>
            )}

            <div className="HContainer align-center mv-20">
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: "bold", color: "#4444B2", mr: 4 }}
              >
                Clasificación
              </Typography>
              <StyledTooltip
                title={classificationTasksTooltip()}
                placement="right"
              >
                <img
                  className="btn-icon-20 pointer "
                  src={icons.info_blue}
                  alt="icon"
                />
              </StyledTooltip>
            </div>
            <List dense>
              {classifications.map((c) => {
                const isActive = activeSection === c.id;
                return (
                  <ListItem
                    button
                    key={c.id}
                    sx={{
                      borderRadius: "8px",
                      backgroundColor: isActive ? "#f0f0ff" : "#fff",
                      border: isActive ? "1px solid #4444B2" : "1px solid #ddd",
                      mb: 1,
                      mt: 1,
                      "&:hover": {
                        backgroundColor: isActive ? "#e8e8ff" : "#f9f9f9"
                      }
                    }}
                    selected={isActive}
                    onClick={() => handleChangeSection(c.id)}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <img
                        src={c.icon}
                        alt={c.label}
                        style={{ width: 18, height: 18 }}
                      />
                    </ListItemIcon>

                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: "bold",
                        flexGrow: 1,
                        color: isActive ? "#4444B2" : "inherit"
                      }}
                    >
                      <span
                        style={{
                          color:
                            c.id === "A"
                              ? "#d30a06ff"
                              : c.id === "B"
                              ? "#ff9800"
                              : c.id === "C"
                              ? "#485ff3ff"
                              : "#2e7d32"
                        }}
                      >
                        {c.id}
                      </span>{" "}
                      - {c.label}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        bgcolor: "#f1f1f5",
                        px: 1.2,
                        borderRadius: "12px",
                        fontSize: "0.75rem",
                        fontWeight: "bold",
                        minWidth: 28,
                        textAlign: "center"
                      }}
                    >
                      {getCountByClassification(c.id)}
                    </Typography>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </Drawer>

        <div className="IVContainer w-100p ">
          {isIndicatorSection(activeSection) ? (
            <Box flex={1} p={isMobile ? 2 : 3} minWidth={0} paddingRight={"0"}>
              {activeSection === "indicadores" && <IndicatorsPanel />}
              {activeSection === "indicadores_gerenciales" && (
                <ManagementIndicatorsPanel
                  isGlobalTaskManagerAdmin={
                    canSeeGlobalManagementIndicators
                  }
                />
              )}
            </Box>
          ) : (
            <DragDropContext
              onDragEnd={handleDragEnd}
            >
              <Box
                flex={1}
                p={isMobile ? 2 : 3}
                minWidth={0}
                paddingRight={"0"}
              >
                {!isSearching ? (
                  <>
                    {/* PENDIENTES */}
                    <TaskSection
                      title={mainTitle}
                      dateRange={mainDateRange}
                      tasks={sortTasksByView(pendingTasks, activeSection)}
                      user={user}
                      setOpenModal={setOpenModal}
                      setSelectedTask={setSelectedTask}
                      showAddButton={["A", "B", "C", "D"].includes(
                        activeSection
                      ) && canCreateTasks}
                      onToggleComplete={handleToggleComplete}
                      onRequestComplete={handleRequestComplete}
                      droppableId={`${activeSection}|pending`}
                      isDraggable={
                        ["A", "B", "C", "D"].includes(activeSection) &&
                        canEditTaskCards
                      }
                      showCheck={activeSection === "diaria" && canCompleteTasks}
                      noEditable={!canEditTaskCards}
                      canEditTask={canEditTask}
                      canDeleteTask={() => canEditTaskCards}
                      onOpenDetails={(task) => {
                        setSelectedTask(task);
                        setOpenComments(true);
                      }}
                      onOpenDelete={(task) => {
                        setSelectedTask(task);
                        setOpenDeleteModal(true);
                      }}
                      titleTooltip={mainTooltip}
                    />

                    {/* PAGINACIÓN PENDIENTES */}
                    {pendingTotalPages > 1 && (
                      <Stack alignItems="center" mt={2} mb={2} spacing={0.5}>
                        <Pagination
                          count={pendingTotalPages}
                          page={pendingPage}
                          onChange={(e, value) => {
                            setPendingPage(value);
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }}
                          color="primary"
                          shape="rounded"
                          size={isMobile ? "small" : "medium"}
                          siblingCount={1}
                          boundaryCount={1}
                          showFirstButton
                          showLastButton
                        />
                      </Stack>
                    )}

                    {/* COMPLETADAS */}
                    {completedTasks.length > 0 && (
                      <>
                        <TaskSection
                          title="Completado"
                          dateRange=""
                          tasks={sortTasksByView(completedTasks, activeSection)}
                          user={user}
                          setOpenModal={setOpenModal}
                          setSelectedTask={setSelectedTask}
                          showAddButton={false}
                          onToggleComplete={handleToggleComplete}
                          onRequestComplete={handleRequestComplete}
                          droppableId={`${activeSection}|completed`}
                          completed={true}
                          isDraggable={["A", "B", "C", "D"].includes(
                            activeSection
                          ) && canEditTaskCards}
                          showCheck={activeSection === "diaria" && canCompleteTasks}
                          noEditable={true}
                          onOpenDetails={(task) => {
                            setSelectedTask(task);
                            setOpenComments(true);
                          }}
                        />

                        {/* PAGINACIÓN COMPLETADAS */}
                        {completedTotalPages > 1 && (
                          <Stack
                            alignItems="center"
                            mt={2}
                            mb={4}
                            spacing={0.5}
                          >
                            <Pagination
                              count={completedTotalPages}
                              page={completedPage}
                              onChange={(e, value) => {
                                setCompletedPage(value);
                                window.scrollTo({
                                  top: 0,
                                  behavior: "smooth"
                                });
                              }}
                              color="primary"
                              shape="rounded"
                              size={isMobile ? "small" : "medium"}
                              siblingCount={1}
                              boundaryCount={1}
                              showFirstButton
                              showLastButton
                            />
                          </Stack>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <TaskSection
                    title="Resultados de búsqueda"
                    dateRange=""
                    tasks={pendingTasks}
                    user={user}
                    setOpenModal={setOpenModal}
                    setSelectedTask={setSelectedTask}
                    showAddButton={false}
                    onToggleComplete={handleToggleComplete}
                    onRequestComplete={handleRequestComplete}
                    droppableId="search|all"
                    isDraggable={false}
                    showCheck={false}
                    noEditable={!canEditTaskCards}
                    canEditTask={canEditTask}
                    canDeleteTask={() => canEditTaskCards}
                  />
                )}
              </Box>
            </DragDropContext>
          )}
        </div>
      </div>
      <DownloadTaskMonthlyModal
        open={openDownloadModal}
        handleClose={() => setOpenDownloadModal(false)}
        yearOptions={taskYearOptions}
        defaultYear={downloadYear}
        defaultMonth={downloadMonth}
        onConfirm={handleDownloadTaskReport}
        loading={downloadLoading}
      />

      <AddTaskModal
        open={openModal}
        handleClose={() => {
          setOpenModal(false);
          setSelectedTask(null);
        }}
        onSubmit={handleSaveFromModal}
        onSaveAttemptEndRef={onSaveAttemptEndRef}
        initialData={selectedTask}
        assignableList={assignableList}
        loadNotificationList={() => loadNotificationList(user_id)}
      />

      <ToastNotification
        severity={toastConfig.severity}
        summary={toastConfig.summary}
        detail={toastConfig.detail}
        showToast={showToast}
        setShowToast={setShowToast}
        position="top-right"
      />

      <CompleteTaskModal
        open={openCompleteModal}
        handleClose={() => {
          setOpenCompleteModal(false);
          setSelectedTask(null);
        }}
        task={{
          id: selectedTask?.id,
          description: selectedTask?.description,
          classification: selectedTask?.calification
        }}
        onConfirm={handleConfirmComplete}
      />

      <TaskDayCommentsModal
        open={openComments}
        onClose={() => setOpenComments(false)}
        task={selectedTask}
        defaultDate={new Date()}
      />

      <DeleteTaskModal
        open={openDeleteModal}
        handleClose={closeDeleteModal}
        task={{
          id: selectedTask?.id,
          description: selectedTask?.description,
          classification: selectedTask?.calification
        }}
        onConfirm={handleConfirmDelete}
      />

      <VerificationModal
        open={openDeleteVerif}
        handleClose={closeDeleteVerification}
        alertText={
          <>
            <b>¿Estás seguro de eliminar la tarea? </b>
            {deleteComment && (
              <p style={{ marginTop: 10, fontSize: "0.9rem", color: "#666" }}>
                Comentario: {deleteComment}
              </p>
            )}
          </>
        }
        onConfirm={() => deleteSelectedTask()}
        iconSeverity="danger"
      />
    </Box>
  );
};

export default OganizeTask;
